import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import SEO from '../components/SEO';
import LandingNav from '../components/Layout/LandingNav';
import LandingFooter from '../components/Layout/LandingFooter';

function PrivacyPolicy() {
  const { theme } = useTheme();
  const d = theme === 'dark';

  return (
    <div className={`min-h-screen flex flex-col ${d ? 'bg-[#0a0a0a]' : 'bg-white'}`}>
      <SEO
        title="Privacy Policy"
        description="Learn how PaperBrainz collects, uses, and protects your data. Read our full privacy policy for our AI customer support chatbot platform."
        path="/privacy-policy"
      />
      <LandingNav />

      <div className="flex-1 pt-32 sm:pt-40 pb-24 sm:pb-32 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16 sm:mb-20 max-w-2xl mx-auto">
            <h1 className={`text-5xl md:text-6xl lg:text-[64px] font-semibold tracking-tight mb-5 ${d ? 'text-white' : 'text-gray-900'}`}>
              Privacy Policy
            </h1>
            <p className={`text-lg md:text-xl font-light leading-relaxed ${d ? 'text-gray-500' : 'text-gray-500'}`}>
              We take your privacy seriously. Learn how we collect, use, and protect your data.
            </p>
            <p className={`text-sm font-light mt-4 ${d ? 'text-gray-600' : 'text-gray-400'}`}>
              Last updated: March 26, 2026
            </p>
          </div>

          {/* Content */}
          <div className="space-y-14 sm:space-y-16">
            <div className="space-y-14 sm:space-y-16">

              {/* Information We Collect */}
              <section>
                <h2 className={`text-2xl md:text-3xl font-semibold tracking-tight mb-4 ${d ? 'text-white' : 'text-gray-900'}`}>
                  Information We Collect
                </h2>
                <p className={`mb-4 ${d ? 'text-gray-400' : 'text-gray-600'}`}>
                  We collect information you provide directly to us and information we collect automatically when you use our services:
                </p>
                <ul className={`list-disc pl-6 space-y-2 ${d ? 'text-gray-400' : 'text-gray-600'}`}>
                  <li><span className={d ? 'text-gray-300' : 'text-gray-700'}>Account information</span> (name, email, company)</li>
                  <li><span className={d ? 'text-gray-300' : 'text-gray-700'}>Knowledge base data</span> (documents, PDFs, FAQs you upload)</li>
                  <li><span className={d ? 'text-gray-300' : 'text-gray-700'}>Chat interaction data</span> (conversations between your chatbot and your customers)</li>
                  <li><span className={d ? 'text-gray-300' : 'text-gray-700'}>Technical information</span> (IP address, browser type, device information)</li>
                  <li><span className={d ? 'text-gray-300' : 'text-gray-700'}>Usage data</span> (feature usage, analytics, performance metrics)</li>
                  <li><span className={d ? 'text-gray-300' : 'text-gray-700'}>Communications</span> (support requests, feedback)</li>
                </ul>
              </section>

              {/* How We Use Your Data */}
              <section>
                <h2 className={`text-2xl md:text-3xl font-semibold tracking-tight mb-4 ${d ? 'text-white' : 'text-gray-900'}`}>
                  How We Use Your Data
                </h2>
                <p className={`mb-4 ${d ? 'text-gray-400' : 'text-gray-600'}`}>
                  We use the information we collect for the following purposes:
                </p>
                <ul className={`list-disc pl-6 space-y-2 ${d ? 'text-gray-400' : 'text-gray-600'}`}>
                  <li>Provide and maintain our AI chatbot services <span className="text-gray-500">(legal basis: contract)</span></li>
                  <li>Index your uploaded knowledge base so your chatbot can answer from it <span className="text-gray-500">(contract)</span></li>
                  <li>Process customer chat interactions through your widget <span className="text-gray-500">(contract)</span></li>
                  <li>Manage your account and subscription <span className="text-gray-500">(contract)</span></li>
                  <li>Provide customer support and respond to inquiries <span className="text-gray-500">(legitimate interest)</span></li>
                  <li>Generate aggregated, non-personal analytics about service usage <span className="text-gray-500">(legitimate interest)</span></li>
                  <li>Comply with legal obligations and prevent abuse <span className="text-gray-500">(legal obligation)</span></li>
                </ul>
              </section>

              {/* How We Protect Your Data */}
              <section>
                <h2 className={`text-2xl md:text-3xl font-semibold tracking-tight mb-4 ${d ? 'text-white' : 'text-gray-900'}`}>
                  How We Protect Your Data
                </h2>
                <p className={`mb-4 ${d ? 'text-gray-400' : 'text-gray-600'}`}>
                  We implement industry-standard security measures to protect your personal information:
                </p>
                <div className="grid md:grid-cols-2 gap-4 mt-4">
                  <div className={`border p-4 rounded-[16px] ${d ? 'bg-white/[0.04] border-white/[0.06]' : 'bg-white border-gray-200'}`}>
                    <h4 className={`font-medium mb-2 ${d ? 'text-white' : 'text-gray-900'}`}>Encryption in Transit & at Rest</h4>
                    <p className="text-sm text-gray-500">All traffic uses TLS 1.2+; stored data is encrypted at rest by our infrastructure providers (Supabase / AWS).</p>
                  </div>
                  <div className={`border p-4 rounded-[16px] ${d ? 'bg-white/[0.04] border-white/[0.06]' : 'bg-white border-gray-200'}`}>
                    <h4 className={`font-medium mb-2 ${d ? 'text-white' : 'text-gray-900'}`}>Access Controls</h4>
                    <p className="text-sm text-gray-500">Strict access controls and authentication for all system access.</p>
                  </div>
                  <div className={`border p-4 rounded-[16px] ${d ? 'bg-white/[0.04] border-white/[0.06]' : 'bg-white border-gray-200'}`}>
                    <h4 className={`font-medium mb-2 ${d ? 'text-white' : 'text-gray-900'}`}>Regular Backups</h4>
                    <p className="text-sm text-gray-500">Automated daily backups with geographic redundancy.</p>
                  </div>
                  <div className={`border p-4 rounded-[16px] ${d ? 'bg-white/[0.04] border-white/[0.06]' : 'bg-white border-gray-200'}`}>
                    <h4 className={`font-medium mb-2 ${d ? 'text-white' : 'text-gray-900'}`}>24/7 Monitoring</h4>
                    <p className="text-sm text-gray-500">Continuous monitoring for security threats and anomalies.</p>
                  </div>
                </div>
              </section>

              {/* Data Sharing */}
              <section>
                <h2 className={`text-2xl md:text-3xl font-semibold tracking-tight mb-4 ${d ? 'text-white' : 'text-gray-900'}`}>
                  Data Sharing and Disclosure
                </h2>
                <p className={`font-medium mb-4 ${d ? 'text-white' : 'text-gray-900'}`}>
                  We never sell your personal data to third parties.
                </p>
                <p className={`mb-4 ${d ? 'text-gray-400' : 'text-gray-600'}`}>
                  We may share your information only in the following limited circumstances:
                </p>
                <ul className={`list-disc pl-6 space-y-2 ${d ? 'text-gray-400' : 'text-gray-600'}`}>
                  <li><span className={d ? 'text-gray-300' : 'text-gray-700'}>Sub-processors:</span> Trusted third-party providers listed below help us operate the service.</li>
                  <li><span className={d ? 'text-gray-300' : 'text-gray-700'}>Legal requirements:</span> When required by law or to protect our legal rights.</li>
                  <li><span className={d ? 'text-gray-300' : 'text-gray-700'}>Business transfers:</span> In connection with a merger, acquisition, or sale of business assets.</li>
                </ul>
              </section>

              {/* Sub-processors */}
              <section>
                <h2 className={`text-2xl md:text-3xl font-semibold tracking-tight mb-4 ${d ? 'text-white' : 'text-gray-900'}`}>
                  Sub-processors
                </h2>
                <p className={`mb-4 ${d ? 'text-gray-400' : 'text-gray-600'}`}>
                  We use the following sub-processors to deliver the service. Each is contractually bound to handle data in line with applicable data protection law.
                </p>
                <div className="overflow-x-auto">
                  <table className={`w-full border-collapse rounded-[12px] overflow-hidden border text-sm ${d ? 'border-white/[0.06]' : 'border-gray-200'}`}>
                    <thead className={d ? 'bg-white/[0.03]' : 'bg-gray-50'}>
                      <tr>
                        <th className={`px-4 py-3 text-left font-medium ${d ? 'text-white' : 'text-gray-900'}`}>Provider</th>
                        <th className={`px-4 py-3 text-left font-medium ${d ? 'text-white' : 'text-gray-900'}`}>Purpose</th>
                        <th className={`px-4 py-3 text-left font-medium ${d ? 'text-white' : 'text-gray-900'}`}>Location</th>
                      </tr>
                    </thead>
                    <tbody className={d ? 'text-gray-400' : 'text-gray-600'}>
                      {[
                        ['Supabase', 'Authentication, database, file storage', 'EU / US'],
                        ['OpenAI', 'AI model that generates chatbot responses (zero data retention configured)', 'US'],
                        ['Qdrant', 'Vector storage for knowledge-base embeddings', 'EU'],
                        ['Lemon Squeezy', 'Payment processing, merchant of record, tax compliance', 'US'],
                        ['Google (Sign-In)', 'Optional OAuth login', 'Global'],
                      ].map(([name, purpose, loc], i) => (
                        <tr key={i} className={`border-t ${d ? 'border-white/[0.06]' : 'border-gray-200'}`}>
                          <td className={`px-4 py-3 ${d ? 'text-gray-300' : 'text-gray-700'}`}>{name}</td>
                          <td className="px-4 py-3">{purpose}</td>
                          <td className="px-4 py-3 whitespace-nowrap">{loc}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className={`text-sm mt-4 ${d ? 'text-gray-500' : 'text-gray-500'}`}>
                  We will notify customers in advance before adding or replacing a sub-processor that materially affects how their data is processed.
                </p>
              </section>

              {/* AI Training */}
              <section>
                <h2 className={`text-2xl md:text-3xl font-semibold tracking-tight mb-4 ${d ? 'text-white' : 'text-gray-900'}`}>
                  We Do Not Train AI Models on Your Data
                </h2>
                <p className={`mb-3 ${d ? 'text-gray-400' : 'text-gray-600'}`}>
                  Your uploaded content, chat transcripts, and customer data are <span className={d ? 'text-white' : 'text-gray-900'}>never</span> used to train PaperBrainz machine-learning models or any general-purpose foundation model.
                </p>
                <p className={`mb-3 ${d ? 'text-gray-400' : 'text-gray-600'}`}>
                  Chat content is sent to OpenAI only to generate the response shown to your visitor. We use OpenAI's API with zero-retention / no-training terms in effect, meaning OpenAI does not retain content beyond what is needed to return the response and does not use it to train their models.
                </p>
                <p className={d ? 'text-gray-400' : 'text-gray-600'}>
                  Your knowledge-base content stays in your dedicated workspace and is only used to answer questions for your chatbot.
                </p>
              </section>

              {/* Your Rights */}
              <section>
                <h2 className={`text-2xl md:text-3xl font-semibold tracking-tight mb-4 ${d ? 'text-white' : 'text-gray-900'}`}>
                  Your Rights and Choices
                </h2>
                <p className={`mb-4 ${d ? 'text-gray-400' : 'text-gray-600'}`}>
                  You have the following rights regarding your personal data:
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className={`border p-4 rounded-[16px] ${d ? 'bg-white/[0.04] border-white/[0.06]' : 'bg-white border-gray-200'}`}>
                    <h4 className={`font-medium mb-2 ${d ? 'text-white' : 'text-gray-900'}`}>Access</h4>
                    <p className="text-sm text-gray-500">Request access to your personal data</p>
                  </div>
                  <div className={`border p-4 rounded-[16px] ${d ? 'bg-white/[0.04] border-white/[0.06]' : 'bg-white border-gray-200'}`}>
                    <h4 className={`font-medium mb-2 ${d ? 'text-white' : 'text-gray-900'}`}>Correction</h4>
                    <p className="text-sm text-gray-500">Update or correct your information</p>
                  </div>
                  <div className={`border p-4 rounded-[16px] ${d ? 'bg-white/[0.04] border-white/[0.06]' : 'bg-white border-gray-200'}`}>
                    <h4 className={`font-medium mb-2 ${d ? 'text-white' : 'text-gray-900'}`}>Deletion</h4>
                    <p className="text-sm text-gray-500">Request deletion of your account and data</p>
                  </div>
                  <div className={`border p-4 rounded-[16px] ${d ? 'bg-white/[0.04] border-white/[0.06]' : 'bg-white border-gray-200'}`}>
                    <h4 className={`font-medium mb-2 ${d ? 'text-white' : 'text-gray-900'}`}>Portability</h4>
                    <p className="text-sm text-gray-500">Export your data in a portable format</p>
                  </div>
                </div>
              </section>

              {/* Data Retention */}
              <section>
                <h2 className={`text-2xl md:text-3xl font-semibold tracking-tight mb-4 ${d ? 'text-white' : 'text-gray-900'}`}>
                  Data Retention
                </h2>
                <p className={`mb-4 ${d ? 'text-gray-400' : 'text-gray-600'}`}>
                  We retain your personal data for as long as your account is active or as needed to provide services. After account deletion, we securely delete personal data within 30 days, except where required by law.
                </p>
                <ul className={`list-disc pl-6 space-y-2 ${d ? 'text-gray-400' : 'text-gray-600'}`}>
                  <li><span className={d ? 'text-gray-300' : 'text-gray-700'}>Account data:</span> Retained while your account is active</li>
                  <li><span className={d ? 'text-gray-300' : 'text-gray-700'}>Knowledge base:</span> Deleted upon account closure or at your request</li>
                  <li><span className={d ? 'text-gray-300' : 'text-gray-700'}>Chat transcripts:</span> Retained per your plan settings (exportable)</li>
                  <li><span className={d ? 'text-gray-300' : 'text-gray-700'}>Billing records:</span> Retained as required by tax and accounting regulations</li>
                </ul>
              </section>

              {/* International Transfers */}
              <section>
                <h2 className={`text-2xl md:text-3xl font-semibold tracking-tight mb-4 ${d ? 'text-white' : 'text-gray-900'}`}>
                  International Data Transfers
                </h2>
                <p className={d ? 'text-gray-400' : 'text-gray-600'}>
                  Your information may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place for international transfers, including compliance with GDPR requirements for transfers outside the EEA.
                </p>
              </section>

              {/* Privacy at a Glance */}
              <section>
                <h2 className={`text-2xl md:text-3xl font-semibold tracking-tight mb-4 ${d ? 'text-white' : 'text-gray-900'}`}>
                  Privacy at a Glance
                </h2>
                <div className={`border rounded-[16px] p-6 ${d ? 'bg-white/[0.04] border-white/[0.06]' : 'bg-white border-gray-200'}`}>
                  <ul className="space-y-3">
                    {[
                      'We never sell your personal data',
                      'We never train AI models on your content',
                      'Encryption in transit (TLS 1.2+) and at rest',
                      'We collect only what we need',
                      'You control your data — export or delete anytime',
                      'GDPR-aligned data handling'
                    ].map((item, i) => (
                      <li key={i} className={`flex items-center ${d ? 'text-gray-300' : 'text-gray-700'}`}>
                        <span className={`w-1 h-1 rounded-full mr-3 flex-shrink-0 ${d ? 'bg-gray-500' : 'bg-gray-400'}`} />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </section>

              {/* Changes to Policy */}
              <section>
                <h2 className={`text-2xl md:text-3xl font-semibold tracking-tight mb-4 ${d ? 'text-white' : 'text-gray-900'}`}>
                  Changes to This Policy
                </h2>
                <p className={d ? 'text-gray-400' : 'text-gray-600'}>
                  We may update this Privacy Policy from time to time. We will notify you of any material changes via email or through our service. Continued use of the service after changes constitutes acceptance of the updated policy.
                </p>
              </section>

              {/* Contact Information */}
              <section className={`border-t pt-10 ${d ? 'border-white/[0.06]' : 'border-gray-200'}`}>
                <h2 className={`text-2xl md:text-3xl font-semibold tracking-tight mb-4 ${d ? 'text-white' : 'text-gray-900'}`}>
                  Contact Us About Privacy
                </h2>
                <p className={`mb-4 ${d ? 'text-gray-400' : 'text-gray-600'}`}>
                  If you have questions about this privacy policy or our data practices, please contact us:
                </p>
                <div className={`border rounded-[16px] p-6 ${d ? 'bg-white/[0.04] border-white/[0.06]' : 'bg-white border-gray-200'}`}>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <h4 className={`font-medium mb-2 ${d ? 'text-white' : 'text-gray-900'}`}>Privacy Officer</h4>
                      <p className={d ? 'text-gray-400' : 'text-gray-600'}>
                        <a href="mailto:support@paperbrainz.com" className={`underline underline-offset-2 decoration-1 transition-colors ${d ? 'text-white hover:text-gray-300' : 'text-gray-900 hover:text-gray-700'}`}>
                          support@paperbrainz.com
                        </a>
                      </p>
                    </div>
                    <div>
                      <h4 className={`font-medium mb-2 ${d ? 'text-white' : 'text-gray-900'}`}>Response Time</h4>
                      <p className={d ? 'text-gray-400' : 'text-gray-600'}>
                        We respond to privacy inquiries within 30 days
                      </p>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>

      <LandingFooter />
    </div>
  );
}

export default PrivacyPolicy;
